import {
  Edit,
  Plus,
  Search,
  Trash2,
  Package,
  Check,
  ShoppingBag,
  Tag
} from 'lucide-react';
import React, { useState, useEffect } from 'react';
import AddItemModal from './ActivityListingComponents/AdditemProduct';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const ActivityListing = () => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editDish, setEditDish] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [priceFilter, setPriceFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    const storedId = localStorage.getItem(`roleid`);
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/product?shop_id=${storedId}`
      );
      setProducts(response.data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Error fetching products');
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = async () => {
    fetchProducts();
    setIsModalOpen(false);
  };

  const handleEditDish = (id) => {
    const productToEdit = products.find((product) => product._id === id);
    setEditDish({
      ...productToEdit,
      image_url: Array.isArray(productToEdit.image_url) ? productToEdit.image_url.join(",") : (productToEdit.image_url || "")
    });
    setIsModalOpen(true);
  };

  const handleSaveDish = async () => {
    setEditDish(null);
    fetchProducts();
    setIsModalOpen(false);
  };

  const handleDeleteDish = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      const token = localStorage.getItem(`token_partner_shop`);
      if (!token) {
        navigate('/login');
        return;
      }
      await axios.delete(`${process.env.REACT_APP_BASE_URL}/product/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchProducts();
      toast.success('Product deleted successfully');
    } catch (error) {
      toast.error('Error deleting product');
    }
  };

  const handleToggleActive = async (id, is_deleted) => {
    try {
      const token = localStorage.getItem(`token_partner_shop`);
      if (!token) {
        navigate('/login');
        return;
      }
      await axios.put(
        `${process.env.REACT_APP_BASE_URL}/product/${id}`,
        { is_deleted: !is_deleted },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProducts(products.map((product) => product._id === id ? { ...product, is_deleted: !is_deleted } : product));
      toast.success(`Product ${!is_deleted ? 'deactivated' : 'activated'} successfully`);
    } catch (error) {
      toast.error('Error updating product status');
    }
  };

  const filteredProducts = products
    .filter((product) => {
      const query = searchTerm.toLowerCase().trim();
      if (!query) return true;
      return (
        (product.name || '').toLowerCase().includes(query) ||
        (product.description || '').toLowerCase().includes(query) ||
        (product.price || '').toString().includes(query)
      );
    })
    .filter((product) => {
      if (!statusFilter) return true;
      return statusFilter === "available" ? !product.is_deleted : product.is_deleted;
    })
    .sort((a, b) => {
      if (priceFilter === "low-to-high") return a.price - b.price;
      if (priceFilter === "high-to-low") return b.price - a.price;
      return 0;
    });

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-96 space-y-4">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        <p className="text-gray-500 font-medium animate-pulse">Loading your inventory...</p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-8 animate-in fade-in duration-700">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h1 className="text-3xl font-extrabold text-primary tracking-tight">Inventory & Products</h1>
          <p className="text-gray-500 font-medium mt-1">Manage and track your shop's product catalog.</p>
        </div>
        <button
          onClick={() => {
            setIsModalOpen(true);
            setEditDish(null);
          }}
          className="px-6 py-4 bg-primary text-white rounded-2xl flex items-center gap-2 font-black shadow-lg shadow-primary/25 hover:scale-105 active:scale-95 transition-all text-sm group"
        >
          <Plus size={20} className="group-hover:rotate-90 transition-transform duration-300" />
          <span>Add New Product</span>
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <div className="bg-white/60 backdrop-blur-xl p-6 rounded-[2.5rem] border border-white/50 shadow-sm flex items-center gap-4 hover:shadow-md transition-all">
          <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center text-primary">
            <Package size={24} />
          </div>
          <div>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Total Products</p>
            <p className="text-2xl font-black text-gray-900">{products.length}</p>
          </div>
        </div>
        <div className="bg-white/60 backdrop-blur-xl p-6 rounded-[2.5rem] border border-white/50 shadow-sm flex items-center gap-4 hover:shadow-md transition-all">
          <div className="w-12 h-12 bg-emerald-500/10 rounded-2xl flex items-center justify-center text-emerald-500">
            <Check size={24} />
          </div>
          <div>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">In Stock</p>
            <p className="text-2xl font-black text-gray-900">{products.filter(p => !p.is_deleted).length}</p>
          </div>
        </div>
        <div className="bg-white/60 backdrop-blur-xl p-6 rounded-[2.5rem] border border-white/50 shadow-sm flex items-center gap-4 hover:shadow-md transition-all">
          <div className="w-12 h-12 bg-amber-500/10 rounded-2xl flex items-center justify-center text-amber-500">
            <Tag size={24} />
          </div>
          <div>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">With Discounts</p>
            <p className="text-2xl font-black text-gray-900">{products.filter(p => p.discounted_price).length}</p>
          </div>
        </div>
      </div>

      <div className="bg-white/60 backdrop-blur-xl p-8 rounded-[3rem] border border-white/50 shadow-sm space-y-6">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[300px] relative group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-primary transition-colors" size={20} />
            <input
              type="text"
              placeholder="Search by product name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-4 bg-gray-50/50 border-none rounded-2xl focus:ring-2 focus:ring-primary/20 transition-all font-medium text-gray-700"
            />
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <select
              value={priceFilter}
              onChange={(e) => setPriceFilter(e.target.value)}
              className="px-4 py-3 bg-gray-50/50 border-none rounded-xl focus:ring-2 focus:ring-primary/20 transition-all font-bold text-gray-600 text-sm min-w-[150px]"
            >
              <option value="">Sort by Price</option>
              <option value="low-to-high">Price: Low to High</option>
              <option value="high-to-low">Price: High to Low</option>
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-3 bg-gray-50/50 border-none rounded-xl focus:ring-2 focus:ring-primary/20 transition-all font-bold text-gray-600 text-sm min-w-[150px]"
            >
              <option value="">All Status</option>
              <option value="available">In Stock</option>
              <option value="unavailable">Out of Stock</option>
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white/60 backdrop-blur-xl rounded-[3rem] border border-white/50 shadow-sm overflow-hidden text-sm uppercase">
        {filteredProducts.length > 0 ? (
          <div className="overflow-x-auto text-[10px]">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="px-8 py-6 font-bold text-gray-400 tracking-widest">Product Information</th>
                  <th className="px-8 py-6 font-bold text-gray-400 tracking-widest text-center">In Stock</th>
                  <th className="px-8 py-6 font-bold text-gray-400 tracking-widest text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 uppercase text-[10px] font-bold">
                {filteredProducts.map((product) => (
                  <tr key={product._id} className="hover:bg-primary/[0.02] transition-colors group">
                    <td className="px-8 py-6">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-2xl bg-gray-50 border border-gray-100 overflow-hidden flex-shrink-0">
                          {product.image_url ? (
                            <img 
                              src={Array.isArray(product.image_url) ? product.image_url[0] : (typeof product.image_url === 'string' ? product.image_url.split(',')[0] : '')} 
                              alt={product.name} 
                              className="w-full h-full object-cover" 
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-300 bg-gray-50">
                              <ShoppingBag size={24} />
                            </div>
                          )}
                        </div>
                        <div className="max-w-xs">
                           <div className="flex items-center gap-2 mb-1">
                             <p className="font-bold text-gray-900 group-hover:text-primary transition-colors text-sm">{product.name}</p>
                             <span className="text-[10px] font-black bg-gray-100 text-gray-400 px-2 py-0.5 rounded-full uppercase tracking-tighter">₹{product.price}</span>
                           </div>
                           <p className="text-[10px] text-gray-400 font-medium truncate normal-case lowercase first-letter:uppercase">{product.description || 'No description'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-8 py-6 text-center">
                      <div className="flex justify-center">
                        <button
                          onClick={() => handleToggleActive(product._id, product.is_deleted)}
                          className={`w-12 h-6 rounded-full p-1 transition-all duration-300 relative ${!product.is_deleted ? 'bg-emerald-500' : 'bg-gray-200'}`}
                        >
                          <div className={`w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-300 transform ${!product.is_deleted ? 'translate-x-6' : 'translate-x-0'}`} />
                        </button>
                      </div>
                    </td>
                    <td className="px-8 py-6 text-right">
                      <div className="flex justify-end gap-2 text-sm uppercase">
                        <button onClick={() => handleEditDish(product._id)} className="p-2 bg-indigo-50 text-indigo-600 rounded-xl hover:bg-indigo-500 hover:text-white transition-all shadow-sm"><Edit size={18} /></button>
                        <button onClick={() => handleDeleteDish(product._id)} className="p-2 bg-rose-50 text-rose-600 rounded-xl hover:bg-rose-500 hover:text-white transition-all shadow-sm"><Trash2 size={18} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-24 px-6 text-center">
            <div className="p-8 bg-gray-100 rounded-full mb-6 text-gray-300"><ShoppingBag size={64} /></div>
            <h3 className="text-xl font-bold text-gray-800 tracking-tight">Your inventory is empty</h3>
            <p className="text-gray-500 text-sm max-w-xs mt-2 font-medium">Add products to your catalog to start selling.</p>
          </div>
        )}
      </div>

      <AddItemModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditDish(null);
        }}
        onAddItem={handleAddItem}
        editDish={editDish}
      />
    </div>
  );
};

export default ActivityListing;
